/**
 * Shift KPI bar (PRD §5.8 S-05, v1.5).
 *
 * Pure-presentational — accepts a `ShiftKpi` object and renders a
 * horizontal row of cards. Used in:
 *   - Day 9 Ops Center bottom strip
 *   - Anywhere else a glance-and-go shift summary is useful
 *
 * Designed for the briefing-room demo: large numbers, sparse colour,
 * one accent per card. The OPERATOR INTERRUPTS card uses the rose
 * accent only when the count > 0 (so a calm shift doesn't draw the
 * eye there).
 */

import {
  Activity,
  AlertCircle,
  Bot,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Zap,
} from "lucide-react";
import type { ShiftKpi } from "@/lib/api";

interface KpiBarProps {
  data: ShiftKpi;
  /** Optional callback when the operator clicks a card (Ops Center filters). */
  onCardClick?: (cardId: KpiCardId) => void;
}

export type KpiCardId =
  | "events"
  | "autoHandled"
  | "interrupts"
  | "falseAlarmRate"
  | "verifyP50"
  | "active"
  | "escalated";

interface CardConfig {
  id: KpiCardId;
  label: string;
  /** Returns the formatted value as a string for display. */
  format: (data: ShiftKpi) => string;
  /** Optional muted-state predicate; muted cards draw less attention. */
  muted?: (data: ShiftKpi) => boolean;
  /** Lucide icon. */
  icon: typeof Zap;
  /** Tailwind colour key for accent. */
  accent: "teal" | "sky" | "rose" | "amber" | "violet" | "emerald" | "slate";
}

function formatPct(v: number | null): string {
  if (v === null) return "—";
  return `${(v * 100).toFixed(0)}%`;
}

function formatSeconds(v: number | null): string {
  if (v === null) return "—";
  if (v < 60) return `${v.toFixed(0)}s`;
  return `${(v / 60).toFixed(1)} min`;
}

const CARDS: CardConfig[] = [
  {
    id: "events",
    label: "Events ingested",
    format: (d) => d.eventsTotal.toLocaleString("en-US"),
    icon: Zap,
    accent: "teal",
  },
  {
    id: "autoHandled",
    label: "Auto-handled",
    format: (d) => d.autoHandled.toLocaleString("en-US"),
    icon: Bot,
    accent: "sky",
  },
  {
    id: "interrupts",
    label: "Operator interrupts",
    format: (d) => d.operatorInterrupts.toLocaleString("en-US"),
    muted: (d) => d.operatorInterrupts === 0,
    icon: AlertCircle,
    accent: "rose",
  },
  {
    id: "falseAlarmRate",
    label: "False alarm rate",
    format: (d) => formatPct(d.falseAlarmRate),
    icon: CheckCircle2,
    accent: "violet",
  },
  {
    id: "verifyP50",
    label: "Median time to verify",
    format: (d) => formatSeconds(d.meanVerifySecondsP50),
    icon: Clock,
    accent: "amber",
  },
  {
    id: "active",
    label: "Active now",
    format: (d) => d.activeCount.toLocaleString("en-US"),
    muted: (d) => d.activeCount === 0,
    icon: Activity,
    accent: "emerald",
  },
  {
    id: "escalated",
    label: "Escalated",
    format: (d) => d.escalatedCount.toLocaleString("en-US"),
    muted: (d) => d.escalatedCount === 0,
    icon: ShieldAlert,
    accent: "rose",
  },
];

const ACTIVE_ACCENT: Record<CardConfig["accent"], string> = {
  teal: "border-teal-700/60 text-teal-200",
  sky: "border-sky-700/60 text-sky-200",
  rose: "border-rose-700/60 text-rose-200",
  amber: "border-amber-700/60 text-amber-200",
  violet: "border-violet-700/60 text-violet-200",
  emerald: "border-emerald-700/60 text-emerald-200",
  slate: "border-slate-700/60 text-slate-200",
};

const MUTED_ACCENT = "border-slate-800/60 text-slate-500";

export function KpiBar({ data, onCardClick }: KpiBarProps) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
      data-testid="kpi-bar"
      aria-label="Shift KPI summary"
    >
      {CARDS.map((c) => {
        const muted = c.muted?.(data) ?? false;
        const Icon = c.icon;
        const accentClasses = muted ? MUTED_ACCENT : ACTIVE_ACCENT[c.accent];
        const Wrapper = onCardClick ? "button" : "div";
        return (
          <Wrapper
            key={c.id}
            type={onCardClick ? "button" : undefined}
            onClick={onCardClick ? () => onCardClick(c.id) : undefined}
            data-testid={`kpi-card-${c.id}`}
            className={
              `rounded-lg border bg-slate-900/40 px-3 py-2.5 text-left ` +
              `transition hover:bg-slate-900/70 ${accentClasses}`
            }
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400">
              <Icon className="size-3" />
              <span>{c.label}</span>
            </div>
            <div className="mt-1 text-2xl font-mono font-semibold leading-none">
              {c.format(data)}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}
